import React, { useState } from 'react';
import { Card, Tabs, Button, Space, Typography, Alert, Divider } from 'antd';
import { UserOutlined, ShopOutlined } from '@ant-design/icons';
import VendorScheduleEditor from './VendorScheduleEditor';
import CustomerScheduleViewer from './CustomerScheduleViewer';

const { Title, Text } = Typography;

function PaymentScheduleDemo() {
    const [activeTab, setActiveTab] = useState('vendor');
    const [demoBookingId] = useState(1); // Demo booking ID

    const tabItems = [
        {
            key: 'vendor',
            label: (
                <span>
                    <ShopOutlined />
                    Vendor View
                </span>
            ),
            children: (
                <div className="space-y-4">
                    <Alert
                        message="Vendor Dashboard - Payment Schedule Management"
                        description="Vendors can create and manage payment schedules, request advance payments, and track milestone payments from customers."
                        type="info"
                        showIcon
                    />
                    <VendorScheduleEditor
                        bookingId={demoBookingId}
                        onScheduleUpdated={() => {
                            console.log('Schedule updated in demo');
                        }}
                    />
                </div>
            ),
        },
        {
            key: 'customer',
            label: (
                <span>
                    <UserOutlined />
                    Customer View
                </span>
            ),
            children: (
                <div className="space-y-4">
                    <Alert
                        message="Customer Booking Details - Payment Schedule"
                        description="Customers can view payment schedules, make milestone payments, and submit reviews after delivery."
                        type="info"
                        showIcon
                    />
                    <CustomerScheduleViewer
                        bookingId={demoBookingId}
                        onPaymentSuccess={() => {
                            console.log('Payment successful in demo');
                        }}
                    />
                </div>
            ),
        },
    ];

    return (
        <div className="max-w-7xl mx-auto p-6">
            <div className="text-center mb-8">
                <Title level={2}>Payment Schedule System Demo</Title>
                <Text type="secondary" className="text-lg">
                    Complete payment management solution for catering bookings
                </Text>
            </div>

            <div className="mb-6">
                <Card>
                    <Title level={4}>Features Overview</Title>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                        <div className="text-center p-4 border rounded">
                            <div className="text-2xl mb-2">📅</div>
                            <Title level={5}>Milestone Payments</Title>
                            <Text type="secondary">Break down large payments into manageable milestones</Text>
                        </div>
                        <div className="text-center p-4 border rounded">
                            <div className="text-2xl mb-2">💰</div>
                            <Title level={5}>Advance Requests</Title>
                            <Text type="secondary">Vendors can request advance payments with reasons</Text>
                        </div>
                        <div className="text-center p-4 border rounded">
                            <div className="text-2xl mb-2">⭐</div>
                            <Title level={5}>Review System</Title>
                            <Text type="secondary">Post-delivery reviews with detailed ratings</Text>
                        </div>
                        <div className="text-center p-4 border rounded">
                            <div className="text-2xl mb-2">🔔</div>
                            <Title level={5}>Real-time Notifications</Title>
                            <Text type="secondary">Instant updates for payments and schedule changes</Text>
                        </div>
                        <div className="text-center p-4 border rounded">
                            <div className="text-2xl mb-2">📊</div>
                            <Title level={5}>Progress Tracking</Title>
                            <Text type="secondary">Visual progress bars and payment status tracking</Text>
                        </div>
                        <div className="text-center p-4 border rounded">
                            <div className="text-2xl mb-2">⏰</div>
                            <Title level={5}>Due Date Management</Title>
                            <Text type="secondary">Overdue payment alerts and deadline tracking</Text>
                        </div>
                    </div>
                </Card>
            </div>

            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                size="large"
            />

            <Divider />

            <Card>
                <Title level={4}>Technical Implementation</Title>
                <div className="space-y-4">
                    <div>
                        <Title level={5}>Backend Features</Title>
                        <ul className="list-disc list-inside space-y-1">
                            <li>Payment schedule management with milestones</li>
                            <li>Advance payment request system</li>
                            <li>Review and rating system</li>
                            <li>Real-time notification system</li>
                            <li>Overdue payment detection</li>
                            <li>Payment status tracking</li>
                        </ul>
                    </div>
                    <div>
                        <Title level={5}>Frontend Components</Title>
                        <ul className="list-disc list-inside space-y-1">
                            <li>VendorScheduleEditor - Complete schedule management</li>
                            <li>CustomerScheduleViewer - Payment interface with reviews</li>
                            <li>PaymentScheduleNotificationService - Real-time updates</li>
                            <li>Integrated with existing notification system</li>
                            <li>Responsive design with Ant Design components</li>
                        </ul>
                    </div>
                </div>
            </Card>
        </div>
    );
}

export default PaymentScheduleDemo;
